import React, { useEffect, useRef, useState } from "react";
import autoTable from "jspdf-autotable";
import "../css/InvoiceGenerator.css";
import "../css/DepositConfirmationGenerator.css";
import { GENERATOR_PACKAGES } from "../utils/eventCalendarDocumentPrefill";
import {
  calculateBookingCharges,
  calculateRemainingBalance,
  formatCurrency,
  getTaxSettingForPaymentMethod,
  roundCurrency,
  TAX_RATE,
  toNonNegativeCurrency,
} from "../utils/bookingCalculations";
import {
  applyDocumentHeader,
  createDocumentPdf,
  DOCUMENT_PDF_TABLE_OPTIONS,
} from "../utils/documentPdf";

import { describePackage } from "../constants/packageDefaults";
import { hasAgreedEventTotal } from "../utils/invoiceCalculations";

const paymentMethods = ["Cash", "Card", "Zelle", "Venmo", "Check", "Other"];

const DepositConfirmationGenerator = ({ prefillRequest = null }) => {
  const [customerName, setCustomerName] = useState("");
  const [childName, setChildName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventTime, setEventTime] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [pricingNeedsReview, setPricingNeedsReview] = useState(false);
  const [agreedEventTotal, setAgreedEventTotal] = useState("");
  const [historicalPackageName, setHistoricalPackageName] = useState("Booking");
  const [additionalCharges, setAdditionalCharges] = useState("");
  const [discount, setDiscount] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const [additionalPaymentAmount, setAdditionalPaymentAmount] = useState("");
  const [depositPaidDate, setDepositPaidDate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [applyTax, setApplyTax] = useState(false);
  const [notes, setNotes] = useState("");
  const [appliedPrefillKey, setAppliedPrefillKey] = useState("");
  const taxWasManuallySet = useRef(false);

  const packageSubtotal = toNonNegativeCurrency(pricingNeedsReview ? agreedEventTotal : selectedPackage?.price);
  const initialDepositPaid = toNonNegativeCurrency(depositAmount);
  const additionalPayment = toNonNegativeCurrency(additionalPaymentAmount);
  const depositPaid = roundCurrency(initialDepositPaid + additionalPayment);
  const totals = calculateBookingCharges({
    baseSubtotal: packageSubtotal,
    additionalCharges: pricingNeedsReview ? 0 : additionalCharges,
    discount: pricingNeedsReview ? 0 : discount,
    applyTax: pricingNeedsReview ? false : applyTax,
  });
  const remainingBalance = calculateRemainingBalance(
    totals.finalEventTotal,
    depositPaid
  );
  const paidInFull =
    totals.finalEventTotal > 0 && remainingBalance <= 0;

  const confirmationStatement = `Kid's Kingdom confirms receipt of ${formatCurrency(
    depositPaid
  )} toward the customer's upcoming event. Total booking charges are ${formatCurrency(
    totals.finalEventTotal
  )}, and the remaining balance is ${formatCurrency(remainingBalance)}.`;

  const resetForm = () => {
    setCustomerName("");
    setChildName("");
    setCustomerPhone("");
    setCustomerEmail("");
    setEventDate("");
    setEventTime("");
    setSelectedPackage(null);
    setPricingNeedsReview(false);
    setAgreedEventTotal("");
    setAdditionalCharges("");
    setDiscount("");
    setDepositAmount("");
    setAdditionalPaymentAmount("");
    setDepositPaidDate("");
    setPaymentMethod("");
    setApplyTax(false);
    taxWasManuallySet.current = false;
    setNotes("");
  };

  useEffect(() => {
    if (!prefillRequest?.requestKey) return;
    if (prefillRequest.requestKey === appliedPrefillKey) return;

    if (!prefillRequest.data) {
      resetForm();
      setAppliedPrefillKey(prefillRequest.requestKey);
      return;
    }

    const next = prefillRequest.data;
    setPricingNeedsReview(Boolean(next.pricingNeedsReview));
    setHistoricalPackageName(next.historicalPackageName || "Booking");
    setAgreedEventTotal("");
    setCustomerName(next.customerName || "");
    setChildName(next.childName || "");
    setCustomerPhone(next.customerPhone || "");
    setCustomerEmail(next.customerEmail || "");
    setEventDate(next.eventDate || "");
    setEventTime(next.eventTime || "");
    setSelectedPackage(
      next.selectedPackage ? { ...next.selectedPackage } : null
    );
    setAdditionalCharges(
      next.additionalCharges !== undefined &&
        next.additionalCharges !== null
        ? String(next.additionalCharges)
        : ""
    );
    setDiscount(
      next.discount !== undefined && next.discount !== null
        ? String(next.discount)
        : ""
    );
    setDepositAmount(
      next.depositAmount !== undefined && next.depositAmount !== null
        ? String(next.depositAmount)
        : ""
    );
    setAdditionalPaymentAmount(
      next.additionalPaymentAmount !== undefined &&
        next.additionalPaymentAmount !== null
        ? String(next.additionalPaymentAmount)
        : ""
    );
    setDepositPaidDate(next.depositPaidDate || "");
    setPaymentMethod(next.paymentMethod || "");
    setApplyTax(
      typeof next.applyTax === "boolean"
        ? next.applyTax
        : getTaxSettingForPaymentMethod(next.paymentMethod)
    );
    taxWasManuallySet.current = false;
    setNotes(next.notes || "");
    setAppliedPrefillKey(prefillRequest.requestKey);
  }, [appliedPrefillKey, prefillRequest]);

  const handlePackageChange = (value) => {
    setSelectedPackage(
      value === "" ? null : { ...GENERATOR_PACKAGES[Number(value)] }
    );
  };

  const handlePackagePriceChange = (value) => {
    setSelectedPackage((current) =>
      current ? { ...current, price: value } : current
    );
  };

  const handlePaymentMethodChange = (value) => {
    setPaymentMethod(value);
    setApplyTax((current) =>
      getTaxSettingForPaymentMethod(
        value,
        current,
        taxWasManuallySet.current
      )
    );
  };

  const handleTaxChange = (checked) => {
    taxWasManuallySet.current = true;
    setApplyTax(checked);
  };

  const generatePDF = () => {
    if (pricingNeedsReview && !hasAgreedEventTotal(agreedEventTotal)) {
      alert("Enter the agreed event total from the original booking before generating this confirmation.");
      return;
    }
    if (!selectedPackage && !pricingNeedsReview) {
      alert("Please select a package before generating the deposit confirmation.");
      return;
    }

    if (depositPaid <= 0) {
      alert("Please enter the deposit amount paid.");
      return;
    }

    const doc = createDocumentPdf();
    applyDocumentHeader(doc, "DEPOSIT CONFIRMATION");

    let infoY = 128;
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 40, infoY);
    infoY += 16;
    doc.text(`Event Date: ${eventDate || "-"}`, 40, infoY);
    infoY += 16;
    if (eventTime) {
      doc.text(`Event Time: ${eventTime}`, 40, infoY);
      infoY += 16;
    }
    doc.text(`Customer: ${customerName || "-"}`, 40, infoY);
    infoY += 16;
    if (childName) {
      doc.text(`Child: ${childName}`, 40, infoY);
      infoY += 16;
    }
    doc.text(`Phone: ${customerPhone || "-"}`, 40, infoY);
    infoY += 16;
    if (customerEmail) {
      doc.text(`Email: ${customerEmail}`, 40, infoY);
      infoY += 16;
    }
    if (paymentMethod) {
      doc.text(`Payment Method: ${paymentMethod}`, 40, infoY);
      infoY += 16;
    }
    if (depositPaidDate) {
      doc.text(`Deposit Paid Date: ${depositPaidDate}`, 40, infoY);
      infoY += 16;
    }

    const rows = pricingNeedsReview ? [
      ["Package", historicalPackageName, ""],
      ["Agreed booking total (includes original tax and discounts)", "", formatCurrency(totals.finalEventTotal)],
    ] : [
      ["Package", selectedPackage.name, ""],
      ["Included", describePackage(selectedPackage), ""],
      ["Package/Base Subtotal", "", formatCurrency(totals.baseSubtotal)],
      ["Additional Charges or Add-Ons", "", formatCurrency(totals.additionalCharges)],
      [`Tax (${(TAX_RATE * 100).toFixed(2)}%) — ${applyTax ? "Applied" : "Not Applied"}`, "", formatCurrency(totals.tax)],
    ];

    if (totals.discount > 0) {
      rows.push(["Discount", "", `-${formatCurrency(totals.discount)}`]);
    }

    rows.push(
      ["Final Event Total", "", formatCurrency(totals.finalEventTotal)],
      ["Deposit Paid", "", `-${formatCurrency(depositPaid)}`],
      ["Remaining Balance", "", formatCurrency(remainingBalance)]
    );

    autoTable(doc, {
      startY: infoY + 12,
      head: [["Description", "Details", "Amount"]],
      body: rows,
      ...DOCUMENT_PDF_TABLE_OPTIONS,
      styles: {
        ...DOCUMENT_PDF_TABLE_OPTIONS.styles,
        valign: "top",
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "left", cellWidth: 180 },
        2: { halign: "right", cellWidth: 140 },
      },
    });

    const statementY = (doc.lastAutoTable?.finalY || infoY + 12) + 24;
    doc.setFontSize(12);
    doc.text("Confirmation Statement", 40, statementY);
    doc.setFontSize(11);
    const statementLines = doc.splitTextToSize(confirmationStatement, 530);
    doc.text(statementLines, 40, statementY + 18);

    if (notes.trim()) {
      const notesY = statementY + 36 + statementLines.length * 12;
      doc.setFontSize(12);
      doc.text("Notes", 40, notesY);
      doc.setFontSize(11);
      const noteLines = doc.splitTextToSize(notes.trim(), 530);
      doc.text(noteLines, 40, notesY + 18);
    }

    doc.setProperties({
      title: `Kiddie Kingdom Deposit Confirmation - ${customerName || ""}`,
      subject: "Deposit Confirmation",
      author: "Kiddie Kingdom Inc",
    });

    doc.save(
      `Kiddie_Kingdom_Deposit_Confirmation_${(
        customerName || "Customer"
      ).replace(/\s+/g, "_")}_${new Date()
        .toLocaleDateString()
        .replace(/\//g, "-")}.pdf`
    );

    resetForm();
  };

  return (
    <div className="invoice-generator deposit-confirmation">
      <h2 className="ig-title">Kiddie Kingdom Deposit Confirmation Generator</h2>
      {prefillRequest?.data?.sourceEventId && (
        <div className="ig-prefill-banner">
          Prefilled from Event Calendar booking `{prefillRequest.data.sourceEventId}`. You
          can review and edit every field before generating the confirmation.
        </div>
      )}

      <div className="ig-grid">
        <div className="ig-card">
          <div className="ig-section">
            <h3 className="ig-section-title">Event & Customer</h3>

            <div className="ig-row">
              <div className="ig-field">
                <label>Customer / Parent Name</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(event) => setCustomerName(event.target.value)}
                  placeholder="e.g., Jane Doe"
                />
              </div>
              <div className="ig-field">
                <label>Child's Name</label>
                <input
                  type="text"
                  value={childName}
                  onChange={(event) => setChildName(event.target.value)}
                  placeholder="e.g., Emma Doe"
                />
              </div>
            </div>

            <div className="ig-row ig-row--name-phone">
              <div className="ig-field">
                <label>Phone Number</label>
                <input
                  type="text"
                  value={customerPhone}
                  onChange={(event) => setCustomerPhone(event.target.value)}
                  placeholder="e.g., 555-123-4567"
                />
              </div>
              <div className="ig-field">
                <label>Email</label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(event) => setCustomerEmail(event.target.value)}
                  placeholder="name@email.com"
                />
              </div>
            </div>

            <div className="ig-row">
              <div className="ig-field">
                <label>Event Date</label>
                <input
                  type="date"
                  value={eventDate}
                  onChange={(event) => setEventDate(event.target.value)}
                />
              </div>
              <div className="ig-field">
                <label>Event Time</label>
                <input
                  type="text"
                  value={eventTime}
                  onChange={(event) => setEventTime(event.target.value)}
                  placeholder="e.g., 10:00 - 12:30"
                />
              </div>
            </div>
          </div>

          <div className="ig-section">
            <h3 className="ig-section-title">Booking Charges</h3>
            {pricingNeedsReview && (
              <div className="ig-field">
                <p>This booking has no saved pricing. Enter its agreed total from the original agreement, including original charges, tax and discounts.</p>
                <label htmlFor="deposit-agreed-total">Agreed event total ($) — {historicalPackageName}</label>
                <input id="deposit-agreed-total" type="number" min="0" step="0.01" value={agreedEventTotal} onChange={(e) => setAgreedEventTotal(e.target.value)} />
              </div>
            )}
            {selectedPackage && <p>{describePackage(selectedPackage)}</p>}


            <div className="ig-row">
              <div className="ig-field">
                <label>Selected Package</label>
                <select
                  disabled={pricingNeedsReview}
                  value={
                    selectedPackage
                      ? GENERATOR_PACKAGES.findIndex(
                          (pkg) => pkg.name === selectedPackage.name
                        )
                      : ""
                  }
                  onChange={(event) => handlePackageChange(event.target.value)}
                >
                  <option value="">Select Package</option>
                  {GENERATOR_PACKAGES.map((pkg, index) => (
                    <option key={pkg.name} value={index}>
                      {pkg.name} - ${selectedPackage?.name === pkg.name ? selectedPackage.price : pkg.price}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ig-field">
                <label>Package/Base Subtotal ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={selectedPackage?.price ?? ""}
                  onChange={(event) =>
                    handlePackagePriceChange(event.target.value)
                  }
                  placeholder="0.00"
                  disabled={pricingNeedsReview || !selectedPackage}
                />
              </div>
            </div>

            <div className="ig-row">
              <div className="ig-field">
                <label>Additional Charges or Add-Ons ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={pricingNeedsReview}
                  value={additionalCharges}
                  onChange={(event) => setAdditionalCharges(event.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="ig-field">
                <label>Discount ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={pricingNeedsReview}
                  value={discount}
                  onChange={(event) => setDiscount(event.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="ig-row">
              <div className="ig-field">
                <label>Deposit Amount Paid ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={depositAmount}
                  onChange={(event) => setDepositAmount(event.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="ig-field">
                <label>Additional Payment Applied ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={additionalPaymentAmount}
                  onChange={(event) =>
                    setAdditionalPaymentAmount(event.target.value)
                  }
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="ig-row">
              <div className="ig-field">
                <label>Date Deposit Was Paid</label>
                <input
                  type="date"
                  value={depositPaidDate}
                  onChange={(event) => setDepositPaidDate(event.target.value)}
                />
              </div>
              <div className="ig-field">
                <label>Payment Method</label>
                <select
                  value={paymentMethod}
                  onChange={(event) =>
                    handlePaymentMethodChange(event.target.value)
                  }
                >
                  <option value="">Select Payment Method</option>
                  {paymentMethods.map((method) => (
                    <option key={method} value={method}>
                      {method}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="ig-row deposit-tax-row">
              <label className="toggle">
                <input
                  type="checkbox"
                  disabled={pricingNeedsReview}
                  checked={applyTax}
                  onChange={(event) => handleTaxChange(event.target.checked)}
                />
                <span className="toggle-slider" />
                <span className="toggle-text">Apply 10.25% Tax</span>
              </label>
              <div className="deposit-tax-help">
                Card defaults tax on; cash, Zelle, and other methods default it off.
              </div>
            </div>

            <div className="ig-row">
              <div className="ig-field">
                <label>Final Event Total</label>
                <input
                  type="text"
                  value={formatCurrency(totals.finalEventTotal)}
                  readOnly
                />
              </div>
              <div className="ig-field">
                <label>Remaining Balance</label>
                <input
                  type="text"
                  value={formatCurrency(remainingBalance)}
                  readOnly
                />
              </div>
            </div>
          </div>

          <div className="ig-section">
            <h3 className="ig-section-title">Notes</h3>
            <div className="ig-field">
              <label>Optional Notes</label>
              <textarea
                className="ig-textarea"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="Add any extra deposit details here."
                rows={4}
              />
            </div>
          </div>

          <div className="ig-section">
            <h3 className="ig-section-title">Confirmation Statement</h3>
            <p className="deposit-statement-preview">{confirmationStatement}</p>
          </div>

          <div className="ig-actions">
            <button className="btn-primary" onClick={generatePDF} disabled={pricingNeedsReview && !hasAgreedEventTotal(agreedEventTotal)}>
              Generate PDF Deposit Confirmation
            </button>
          </div>
        </div>

        <aside className="ig-summary">
          <div className="summary-card">
            <h3>Summary</h3>
            <div className="summary-row">
              <span>Selected Package</span>
              <strong>{selectedPackage?.name || (pricingNeedsReview ? historicalPackageName : "-")}</strong>
            </div>
            <div className="summary-row">
              <span>{pricingNeedsReview ? "Agreed Event Total" : "Package/Base Subtotal"}</span>
              <strong>{formatCurrency(totals.baseSubtotal)}</strong>
            </div>
            <div className="summary-row">
              <span>Additional Charges or Add-Ons</span>
              <strong>{formatCurrency(totals.additionalCharges)}</strong>
            </div>
            {totals.discount > 0 && (
              <div className="summary-row">
                <span>Discount</span>
                <strong>-{formatCurrency(totals.discount)}</strong>
              </div>
            )}
            {!pricingNeedsReview && <div className="summary-row">
              <span>Tax {applyTax ? "(10.25%)" : "(Not Applied)"}</span>
              <strong>{formatCurrency(totals.tax)}</strong>
            </div>}
            <div className="summary-row">
              <span>Final Event Total</span>
              <strong>{formatCurrency(totals.finalEventTotal)}</strong>
            </div>
            <div className="summary-row">
              <span>Deposit Paid</span>
              <strong>{formatCurrency(depositPaid)}</strong>
            </div>
            <div className="summary-row">
              <span>Deposit Date</span>
              <strong>{depositPaidDate || "-"}</strong>
            </div>
            <div className="summary-row">
              <span>Payment Method</span>
              <strong>{paymentMethod || "-"}</strong>
            </div>
            <div className="summary-row">
              <span>Payment Status</span>
              <strong>{paidInFull ? "Paid in Full" : "Balance Due"}</strong>
            </div>

            <div className="summary-total">
              <div>Remaining Balance</div>
              <div className="summary-amount">{formatCurrency(remainingBalance)}</div>
            </div>

            <div className="deposit-summary-statement">{confirmationStatement}</div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default DepositConfirmationGenerator;
